import { ReactNode, useEffect } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'

import { useWeb3React, UnsupportedChainIdError } from '@web3-react/core'
import {
  UserRejectedRequestError as UserRejectedRequestErrorInjected,
} from '@web3-react/injected-connector'
import {
  UserRejectedRequestError as UserRejectedRequestErrorWalletConnect,
} from '@web3-react/walletconnect-connector'
import { Web3Provider } from '@ethersproject/providers'

import { useAppSelector, useAppDispatch } from '../app/hooks'
import { setLoaded, authorize, deauthorize } from '../features/wallet/walletSlice'
import { closeModal, ModalType, openModal } from '../features/modal/modalSlice'
import { connectorsByName } from '../helpers/connectors'

const MetaMaskProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const dispatch = useAppDispatch()
  const {
    active: networkActive, account, chainId, error: networkError, activate: activateNetwork,
  } = useWeb3React<Web3Provider>()
  const isUnsupportedChainIdError = networkError instanceof UnsupportedChainIdError
  const isRejectedError = (
    networkError instanceof UserRejectedRequestErrorInjected
    || networkError instanceof UserRejectedRequestErrorWalletConnect
    || networkError?.message.includes('User denied account authorization')
  )

  const { loaded: isLoaded, forcedExit, connector } = useAppSelector((state) => state.wallet)

  const injected = (connector) ? connectorsByName[connector] : undefined

  const modalOpened = useAppSelector(
    (state) => Object.keys(state.modal).filter((key) => state.modal[key as unknown as ModalType] === true).length > 0,
  )
  const isUnsupportedModalOpened = useAppSelector(
    (state) => state.modal[ModalType.WRONG_NETWORK_MODAL],
  )

  useEffect(() => {
    if (isUnsupportedChainIdError && modalOpened) {
      dispatch(closeModal())
      dispatch(openModal(ModalType.WRONG_NETWORK_MODAL))
    } else if (!isUnsupportedChainIdError && isUnsupportedModalOpened) {
      dispatch(closeModal())
    }
  }, [modalOpened, dispatch, isUnsupportedChainIdError, isUnsupportedModalOpened])

  useEffect(() => {
    (async () => {
      if (forcedExit) return

      if (!injected) {
        console.log('injector not found')
        dispatch(setLoaded(true))
        return
      }

      console.group(`Got connector: ${connector}`)

      if (isRejectedError) {
        console.log('rejecting.')
        console.groupEnd()
        dispatch(deauthorize())
        return
      }

      let isAuthorized = true
      if ('isAuthorized' in injected) {
        console.log('Authorizing...')
        dispatch(setLoaded(true))
        try {
          isAuthorized = await injected.isAuthorized()
        } catch (e) {
          console.log('error in authorization', e)
          console.groupEnd()
          dispatch(deauthorize())
          return
        }
      }

      console.log(
        'Authorized',
        isAuthorized,
        'account',
        account,
        'chainId',
        chainId,
        'networkActive',
        networkActive,
        'networkError',
        networkError,
      )

      if (!isAuthorized) {
        console.groupEnd()
        dispatch(deauthorize())
        return
      }

      if (isAuthorized && !networkActive && !networkError) {
        try {
          await activateNetwork(injected)
        } catch (e) {
          console.log('error in activation', e)
          console.groupEnd()
          dispatch(deauthorize())
          return
        }
      }

      if (isAuthorized && account && chainId) {
        dispatch(authorize({ account, chainId }))
      } else {
        dispatch(setLoaded(true))
      }
      console.groupEnd()
    })()
  }, [
    dispatch,
    isRejectedError,
    account,
    connector,
    injected,
    chainId,
    activateNetwork,
    networkActive,
    networkError,
    forcedExit,
  ])
  if (isLoaded) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>
  }
  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <CircularProgress />
      </Box>
    </Container>
  )
}

export default MetaMaskProvider
